import { MenuItem, Stack, TextField, Typography } from "@mui/material";
import { ChangeEvent, FormEvent, useCallback } from "react";
import { usePrescribe } from "src/context/PrescribeProvider/usePrescribe";
import { useStepper } from "src/context/StepperProvider/useStepper";
import StepsFooter from "../StepsFooter";
import { useSelectableDosages } from "src/hooks/useSelectableDosages";
import { useDosageInstructions } from "src/hooks/useDosageInstructions";
import { usePrescription } from "src/context/PrescriptionProvider";

interface Props {
  dosage: string;
  index: number;
}

const DosageInput = ({ dosage, index }: Props) => {
  const product_id = usePrescription((p) => p.product_id);
  const { setDosages, medication, dosages } = usePrescribe();
  const { durations } = useDosageInstructions(
    medication,
    medication?.pharmacy.name ?? "",
    dosages,
  );
  const selectableDosages = useSelectableDosages(product_id, medication?.form);

  const handleDosage = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setDosages((dosages) => {
        return [
          ...dosages.slice(0, index),
          event.target.value,
          ...dosages.slice(index + 1),
        ];
      });
    },
    [index, setDosages],
  );

  return (
    <Stack gap={1}>
      {medication?.form === "injectable" ? (
        <Typography>{durations[index]}</Typography>
      ) : null}
      <TextField
        select
        value={dosage}
        onChange={handleDosage}
        label="Dosage"
        required
      >
        {selectableDosages.map((dosage) => (
          <MenuItem key={dosage} value={dosage}>
            {dosage}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
};

const SelectInstructionsStep = () => {
  const { handleNext } = useStepper();
  const { dosages } = usePrescribe();

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      handleNext();
    },
    [handleNext],
  );

  return (
    <Stack flexGrow={1} component="form" onSubmit={handleSubmit}>
      <Stack gap={2}>
        {dosages.map((dosage, index) => (
          <DosageInput
            key={`${index}-${dosage}`}
            dosage={dosage}
            index={index}
          />
        ))}
      </Stack>
      <StepsFooter />
    </Stack>
  );
};

export default SelectInstructionsStep;
